/**
 * @author Gillis Haasnoot <gillis.haasnoot@gmail.com>
 * @package Banana
 * @summary Page
 */

import {Panel} from "./Panel.js";
import {Control} from "./Control.js";
import {DomHelper} from "../util/DomHelper.js";
import {arrayInteratorAsync, flattenTreeDepthFirst} from "../util/Utils.js";

export class Page extends Panel {
    constructor() {
        super();
        this.validators = [];
        this.dataSets = new Map();
        this.uniqueId = 0;

        // Bind event handlers
        this.resizeHandler = this.onWindowResize.bind(this);
        this.visibilityHandler = this.onVisibilityChange.bind(this);
        this.offlineHandler = this.onOffline.bind(this);
        this.onlineHandler = this.onOnline.bind(this);
    }

    init() {
        super.init();
        this.addCssClass('BPage');

        window.addEventListener("resize", this.resizeHandler);
        document.addEventListener("visibilitychange", this.visibilityHandler);
        window.addEventListener("offline", this.offlineHandler);
        window.addEventListener("online", this.onlineHandler);
    }

    setApplication(app) {
        this.application = app;
    }

    getApplication() {
        return this.application;
    }

    run(target) {
        this.validationControls = {};
        this.initRender(this, target || this.getApplication().settings.renderTarget);
    }

    initRender(control, target, place, wasRerendering, parentRendering, index) {
        this.rendering = true;

        if (wasRerendering) {
            this.renderControl(control, target, place, index);
            this.rendering = false;
            this.recursiveRegisterEvents(control);
            return;
        }

        this.isRendered = false;
        this.initializeControl(control, target);
        this.renderControl(control, target, place, index);
        this.isRendered = true;
        this.setVisible(true);

        if (control?.triggerEvent) {
            control.triggerEvent('renderComplete', this);
        }

        if (!parentRendering) {
            this.recursiveRegisterEvents(control);
            this.rendering = false;
            this.parentFirstTimeRendering = false;
        }

        this.recursiveUpdateDisplay(control);
    }

    rerender(control) {
        const parentRerendering = this.isRerendering;
        const parentRendering = this.rendering;

        if (parentRendering && !parentRerendering) {
            this.parentFirstTimeRendering = true;
        }

        this.isRerendering = true;

        if (!parentRendering) {
            this.recursiveUnRegisterEvents(control);
        }

        const orig = control.getFirstUiControl();
        const parentControl = orig.getParent();

        this.initRender(control, parentControl, orig, parentRerendering, parentRendering);
        this.isRerendering = parentRerendering;
    }

    setContentPlaceHolder(ph) {
        this.contentPlaceHolder = ph;
    }

    getContentPlaceHolder() {
        return this.contentPlaceHolder;
    }

    setPageTemplate(ph) {
        this.contentPlaceHolder = ph;
    }

    getPageTemplate() {
        return this.contentPlaceHolder;
    }

    hideLoader() {
        this.loader?.hide();
    }

    addDataSet(id, dataset) {
        if (!this.dataSets.has(id)) {
            dataset.id = id;
            this.dataSets.set(id, dataset);
        }
    }

    getDataSet(id) {
        return this.dataSets.get(id);
    }

    removeDataSets() {
        this.dataSets.forEach(ds => ds.clear());
        this.dataSets.clear();
    }

    isValid() {
        return Banana.Util.ValidationManager.validateAll();
    }

    initializeControl(control, target) {
        if (!(control instanceof Control)) return;

        if (!control.clientId) {
            control.setClientId(target instanceof Control
                ? `b${++window.domuuid}`
                : `${this.getApplication().settings.applicationName || ''}${this.uniqueId++}`);
        }

        control.setParent(target);
        control.setPage(this);

        if (!control.isInitialized) {
            control.createComponents();
            control.isInitialized = true;

            const bindSource = control.bindedDataSource;
            const bindData = control.bindedData;

            if (bindSource && this.dataSets.has(bindSource[0])) {
                this.dataSets.get(bindSource[0]).bindControlToDataSource(control);
            }
            if (bindData && this.dataSets.has(bindData[0])) {
                this.dataSets.get(bindData[0]).bindControlToData(control);
            }
        }

        control.getControls().forEach(child => this.initializeControl(child, control));
    }

    renderControl(control, target, place, index) {
        const data = control instanceof Control ? control.getHtml(true) : control;

        if (target instanceof Control) target.cachedElement = undefined;
        if (control instanceof Control) control.cachedElement = undefined;

        if (index !== undefined) {
            if (target.controls[index]) {
                DomHelper.renderBefore(data, target.controls[index]);
            } else {
                console.error("Cannot add control at index", index);
                DomHelper.render(data, target);
            }
        } else if (place) {
            DomHelper.replace(data, place);
        } else {
            DomHelper.render(data, target);
        }
    }

    recursiveUpdateDisplay(control) {
        if (!(control instanceof Control)) return;

        control.isRendered = true;
        control.updateDisplay();
        control.getControls().forEach(child => this.recursiveUpdateDisplay(child));
    }

    recursiveRegisterEvents(control) {
        if (!(control instanceof Control)) return;

        control.registerEvents();
        control.getControls().forEach(child => this.recursiveRegisterEvents(child));
    }

    recursiveUnRegisterEvents(control) {
        if (!(control instanceof Control)) return;

        control.onPreInvalidateContents();
        control.unregisterEvents();
        control.getControls().forEach(child => this.recursiveUnRegisterEvents(child));
    }

    removeControl(control, dontRemoveDom) {
        if (!(control instanceof Control)) return;

        control.controls.slice().reverse().forEach(child => {
            this.removeControl(child, true);
            control.controls.pop();
        });

        control.unregisterEvents();
        control.unload();

        if (!dontRemoveDom) DomHelper.remove(control);

        const parent = control.parent;
        if (parent) {
            const index = parent.controls.indexOf(control);
            if (index !== -1) parent.controls.splice(index, 1);
        }
    }

    clearControl(control) {
        const childs = control.getControls().slice();
        childs.forEach(child => this.removeControl(child, true));
        control.controls = [];

        if (control.cachedElement) {
            control.cachedElement.empty();
        } else {
            jQuery(`#${control.getClientId()}`).empty();
        }
    }

    remove(keepPageDom, cb) {
        const flat = flattenTreeDepthFirst(this, 'controls').slice(0, -1);

        arrayInteratorAsync(flat, (control) => {
            if (!(control instanceof Control)) return;

            control.unregisterEvents();
            control.unload();

            const parent = control.parent;
            if (parent?.controls) {
                const index = parent.controls.indexOf(control);
                if (index !== -1) parent.controls.splice(index, 1);
            }
        }, 0, () => {
            this.unload();
            this.clearIds();

            window.removeEventListener("resize", this.resizeHandler);
            document.removeEventListener("visibilitychange", this.visibilityHandler);
            window.removeEventListener("offline", this.offlineHandler);
            window.removeEventListener("online", this.onlineHandler);

            this.unbind();
            this.removeDataSets();
            clearTimeout(this.timer);

            if (!keepPageDom) DomHelper.remove(this);
            cb?.();
        });
    }

    removeDom() {
        jQuery('#cleared').remove();
    }

    clearProps() {
        // Original commented implementation preserved
        // for (const prop in this) {
        //     this[prop] = undefined;
        // }
    }

    onWindowResize(control = this) {
        if (!(control instanceof Control)) return;
        control.onWindowResize();
        control.getControls().forEach(child => this.onWindowResize(child));
    }

    onVisibilityChange(control = this) {
        if (!(control instanceof Control)) return;
        control.onVisibilityChange();
        control.getControls().forEach(child => this.onVisibilityChange(child));
    }

    onOffline(control = this) {
        if (!(control instanceof Control)) return;
        control.onOffline();
        control.getControls().forEach(child => this.onOffline(child));
    }

    onOnline(control = this) {
        if (!(control instanceof Control)) return;
        control.onOnline();
        control.getControls().forEach(child => this.onOnline(child));
    }

    clearIds(control = this) {
        DomHelper.clearIdsFrom(control.getClientId());
    }
}